/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LogOut, Search, MessageCircle, MapPin, Phone, Mail, CheckCircle2, Sparkles, RefreshCw, Flame,
} from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import JellyLogo from '@/components/JellyLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'jelly_team_token';

// WhatsApp templates per tier — short and easy to send during the fair.
const TEMPLATES = {
  hot: [
    {
      id: 'hot-distrib',
      title: 'Distribución / Mayorista',
      es: (first) => `Hola ${first}! 🍓 Soy del equipo de Jelly by Enkor 💕 Vimos que te registraste como mayorista/distribuidor en Expobelleza 2026. Me encantaría coordinar una llamada de 10 min para mostrarte el catálogo, precios especiales y márgenes. ¿Cuándo te queda bien? ✨`,
      en: (first) => `Hi ${first}! 🍓 Jelly by Enkor team here 💕 We saw you registered as a wholesaler at Expobelleza 2026. I'd love a quick 10-min call to share our catalog, special pricing & margins. When works for you? ✨`,
    },
    {
      id: 'hot-followup',
      title: 'Seguimiento post-visita',
      es: (first) => `${first}! Fue un placer conocerte en el stand 🍓💕 Te paso el link del catálogo digital y precios mayorista. Cualquier duda escríbeme directo, aquí estoy. — Equipo Jelly`,
      en: (first) => `${first}! Great meeting you at our booth 🍓💕 Here's our wholesale catalog & pricing. DM me anytime. — Jelly Team`,
    },
  ],
  warm: [
    {
      id: 'warm-monday',
      title: 'Seguimiento lunes',
      es: (first) => `Hola ${first} 💕 ¿Cómo estás? Te escribo del equipo de Jelly by Enkor 🍓 — vi tu registro en Expobelleza. ¿Te gustaría que te envíe el catálogo + lista de precios para que veas qué te late? ✨`,
      en: (first) => `Hi ${first} 💕 Jelly by Enkor team here 🍓 — saw your Expobelleza signup. Want me to send the catalog & price list so you can browse? ✨`,
    },
    {
      id: 'warm-promo',
      title: 'Promo personalizada',
      es: (first) => `${first} 🎀 Solo para ti: 15% OFF en tu primer pedido Jelly. Código: HOLA15 (vence en 5 días). Catálogo aquí: jellybyenkor.com 💄`,
      en: (first) => `${first} 🎀 Just for you: 15% OFF your first Jelly order. Code: HOLA15 (5-day expiry). Catalog: jellybyenkor.com 💄`,
    },
  ],
  cold: [
    {
      id: 'cold-welcome',
      title: 'Bienvenida + cupón',
      es: (first) => `Hola ${first} 💕 Gracias por pasarte por Jelly by Enkor en Expobelleza 🍓 Te dejamos un mimito: 10% OFF con el código JELLY10 en jellybyenkor.com ✨`,
      en: (first) => `Hi ${first} 💕 Thanks for stopping by Jelly by Enkor at Expobelleza 🍓 Treat: 10% OFF with code JELLY10 at jellybyenkor.com ✨`,
    },
    {
      id: 'cold-newsletter',
      title: 'Newsletter / contenido',
      es: () => `¿Sabías que la base jelly se puede usar también de blush? 🍓 Mira el tutorial en nuestro IG @jellybyenkor — y compártelo con tu amiga jelly girl 💕`,
      en: () => `Did you know jelly base doubles as blush? 🍓 Watch the tutorial on IG @jellybyenkor — share with your jelly bestie 💕`,
    },
  ],
};

const tierMeta = {
  hot:  { label: '🔥 HOT',  pillCls: 'bg-[#FF2D9C] text-white',    cardCls: 'border-[#FF2D9C]/40 bg-gradient-to-br from-[#FFE4EC] to-[#FAEFC2]/40' },
  warm: { label: '💎 WARM', pillCls: 'bg-[#F4DD8C] text-[#1F1A24]', cardCls: 'border-[#F4DD8C] bg-[#FFFCEC]' },
  cold: { label: '💕 COLD', pillCls: 'bg-[#F0EBFC] text-[#A99BE0]', cardCls: 'border-[#A99BE0]/30 bg-white' },
};

const normalizePhoneForWa = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length === 10 ? `57${digits}` : digits;
};

const buildWaLink = (phone, text) =>
  `https://wa.me/${normalizePhoneForWa(phone)}?text=${encodeURIComponent(text)}`;

export default function Team() {
  const { lang } = useLang();
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [password, setPassword] = useState('');
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(null);
  const [filterTier, setFilterTier] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null); // {lead} | null

  const headers = useMemo(() => ({ 'X-Team-Token': token }), [token]);

  // Debounce search input → 300ms (avoid hammering backend on every keystroke during the fair)
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filterTier) params.set('tier', filterTier);
        if (filterCity) params.set('city', filterCity);
        if (debouncedSearch.trim().length >= 2) params.set('q', debouncedSearch.trim());
        const qs = params.toString();
        const [r, c, cts] = await Promise.all([
          axios.get(`${API}/team/registrations${qs ? `?${qs}` : ''}`, { headers }),
          axios.get(`${API}/team/live-count`, { headers }),
          axios.get(`${API}/team/cities`, { headers }),
        ]);
        if (!cancelled) {
          setItems(r.data || []);
          setCount(c.data);
          setCities(cts.data || []);
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY);
          setToken('');
          toast.error(lang === 'es' ? 'Sesión expirada' : 'Session expired');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 8000); // Lightweight refresh every 8s
    return () => { cancelled = true; clearInterval(id); };
  }, [token, filterTier, filterCity, debouncedSearch, headers, lang]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/team/login`, { password });
      localStorage.setItem(STORAGE_KEY, data.token);
      setToken(data.token);
      toast.success(lang === 'es' ? '¡Hola equipo!' : 'Hi team!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'es' ? 'Contraseña incorrecta' : 'Wrong password'));
    }
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setItems([]);
    setCount(null);
  };

  const markVisited = async (lead) => {
    const newVal = !lead.visited_stand;
    try {
      const { data } = await axios.patch(
        `${API}/team/registrations/${lead.id}/visited?value=${newVal}`,
        null,
        { headers }
      );
      setItems((arr) => arr.map((it) => (it.id === lead.id ? data : it)));
      toast.success(newVal
        ? (lang === 'es' ? '✓ Marcada como visitó stand' : '✓ Marked as visited stand')
        : (lang === 'es' ? 'Etiqueta removida' : 'Tag removed'));
    } catch (err) {
      toast.error(lang === 'es' ? 'No se pudo actualizar' : 'Could not update');
    }
  };

  if (!token) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center px-6 py-12 bg-gradient-to-br from-[#FFE6EB] via-[#FFFBFB] to-[#F0EBFC]">
        <form
          data-testid="team-login-form"
          onSubmit={login}
          className="w-full max-w-md rounded-[2rem] bg-white border border-[#A99BE0]/20 p-8 shadow-[0_24px_60px_rgba(169,155,224,0.18)]"
        >
          <JellyLogo size="lg" color="#FF2D9C" bylineColor="#FF2D9C" />
          <p className="mt-6 text-[10px] font-bold tracking-[0.3em] uppercase text-[#A99BE0]">PANEL EQUIPO COMERCIAL</p>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter">
            {lang === 'es' ? 'Ventas en feria' : 'Sales on the floor'}
          </h1>
          <p className="mt-2 text-sm text-[#574A66]">
            {lang === 'es'
              ? 'Acceso rápido a leads con plantillas WhatsApp.'
              : 'Quick lead access with WhatsApp templates.'}
          </p>
          <input
            data-testid="team-password"
            type="password"
            placeholder={lang === 'es' ? 'Contraseña del equipo' : 'Team password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-8 w-full rounded-2xl border border-[#A99BE0]/30 bg-[#FFFBFB] px-4 py-3 text-sm focus:outline-none focus:border-[#A99BE0] focus:ring-2 focus:ring-[#A99BE0]/20"
            required
          />
          <button
            data-testid="team-submit"
            type="submit"
            className="mt-4 w-full inline-flex justify-center items-center px-6 py-3 rounded-full bg-[#FF2D9C] text-white text-sm font-bold tracking-widest uppercase hover:bg-[#FF5C83] transition-colors"
          >
            {lang === 'es' ? 'Entrar' : 'Sign in'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-[#FFFBFB] min-h-[100dvh] pb-32" data-testid="team-dashboard">
      {/* Compact mobile-first header */}
      <header className="sticky top-0 z-30 bg-[#1F1A24] text-white px-4 py-3 shadow-md">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-[#F4DD8C] shrink-0" />
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase truncate">
              {lang === 'es' ? 'Equipo · Expobelleza' : 'Team · Expobelleza'}
            </p>
          </div>
          <button
            data-testid="team-logout"
            onClick={logout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-[10px] font-bold tracking-widest uppercase hover:bg-white/20 transition-colors"
          >
            <LogOut className="w-3 h-3" /> {lang === 'es' ? 'Salir' : 'Exit'}
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Live counter ribbon — minimal, fast */}
        {count && (
          <div data-testid="team-counter" className="rounded-3xl bg-gradient-to-r from-[#FF2D9C] via-[#FF7597] to-[#A99BE0] p-[2px]">
            <div className="rounded-[1.4rem] bg-white px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF2D9C] opacity-75 animate-ping"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF2D9C]"></span>
                </span>
                <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#574A66]">EN VIVO</p>
                <p data-testid="team-total" className="font-display text-2xl font-black tracking-tighter text-[#1F1A24] ml-2 tabular-nums">{count.total}</p>
              </div>
              <div className="flex gap-2 text-[10px] font-bold tracking-widest uppercase">
                <Stat color="bg-[#FF2D9C] text-white" label={`🔥 ${count.hot}`} />
                <Stat color="bg-[#F4DD8C] text-[#1F1A24]" label={`💎 ${count.warm}`} />
                <Stat color="bg-[#F0EBFC] text-[#7C5DA5]" label={`💕 ${count.cold}`} />
              </div>
            </div>
          </div>
        )}

        {/* Search + filters — sticky on scroll */}
        <div className="mt-3 sticky top-[52px] z-20 bg-[#FFFBFB]/95 backdrop-blur py-2 -mx-4 px-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A99BE0]" />
            <input
              data-testid="team-search"
              type="search"
              placeholder={lang === 'es' ? 'Buscar nombre o teléfono…' : 'Search name or phone…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-3 rounded-full border border-[#A99BE0]/30 bg-white text-sm focus:outline-none focus:border-[#A99BE0]"
            />
          </div>
          <div className="mt-2 flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
            {[
              { v: '', l: lang === 'es' ? 'TODOS' : 'ALL' },
              { v: 'hot', l: '🔥 HOT' },
              { v: 'warm', l: '💎 WARM' },
              { v: 'cold', l: '💕 COLD' },
            ].map((opt) => (
              <button
                key={opt.v || 'all'}
                data-testid={`team-tier-${opt.v || 'all'}`}
                onClick={() => setFilterTier(opt.v)}
                className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase transition-colors whitespace-nowrap ${
                  filterTier === opt.v ? 'bg-[#1F1A24] text-white' : 'bg-white border border-[#A99BE0]/20 text-[#1F1A24]'
                }`}
              >
                {opt.l}
              </button>
            ))}
            <select
              data-testid="team-city"
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="shrink-0 px-3 py-1.5 rounded-full text-[10px] font-bold tracking-widest uppercase bg-white border border-[#A99BE0]/30 text-[#1F1A24]"
            >
              <option value="">📍 {lang === 'es' ? 'TODAS' : 'ALL'}</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Lead cards (mobile-first) */}
        <div className="mt-4 grid gap-3">
          {loading && items.length === 0 && (
            <p className="text-sm text-[#574A66] flex items-center gap-2"><RefreshCw className="w-3 h-3 animate-spin" /> {lang === 'es' ? 'Cargando…' : 'Loading…'}</p>
          )}
          {!loading && items.length === 0 && (
            <p data-testid="team-empty" className="text-sm text-[#574A66] italic">
              {lang === 'es' ? 'Sin resultados.' : 'No results.'}
            </p>
          )}
          {items.map((it) => (
            <LeadCard
              key={it.id}
              lead={it}
              lang={lang}
              onContact={() => setPickerOpen({ lead: it })}
              onMarkVisited={() => markVisited(it)}
            />
          ))}
        </div>
      </div>

      {/* Bottom sheet: WhatsApp template picker */}
      {pickerOpen && (
        <TemplatePicker
          lead={pickerOpen.lead}
          lang={lang}
          onClose={() => setPickerOpen(null)}
        />
      )}
    </div>
  );
}

const Stat = ({ color, label }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full ${color}`}>{label}</span>
);

const LeadCard = ({ lead, lang, onContact, onMarkVisited }) => {
  const tier = (lead.tier || 'cold').toLowerCase();
  const meta = tierMeta[tier] || tierMeta.cold;
  const visited = !!lead.visited_stand;
  return (
    <article
      data-testid={`team-card-${lead.id}`}
      className={`rounded-3xl border-2 p-4 shadow-sm ${meta.cardCls}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-black tracking-tighter text-[#1F1A24] truncate">
              {lead.name}
            </h3>
            <span className={`text-[9px] font-bold tracking-widest uppercase rounded-full px-2 py-0.5 whitespace-nowrap ${meta.pillCls}`}>
              {meta.label}
            </span>
            <span className="text-[10px] font-display font-black text-[#FF2D9C] tabular-nums">
              {lead.lead_score ?? 0}pts
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#574A66]">
            <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{lead.phone || '—'}</span>
            {lead.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{lead.email}</span>}
            {lead.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.city}</span>}
          </div>
          {lead.stand_code && (
            <p className="mt-1 text-[10px] font-bold tracking-widest uppercase text-[#A99BE0]">
              📍 STAND {lead.stand_code}
            </p>
          )}
          {Array.isArray(lead.tags) && lead.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {lead.tags.map((t) => (
                <span key={t} className="text-[9px] font-bold tracking-widest uppercase rounded-full px-2 py-0.5 bg-white/80 text-[#7C5DA5] border border-[#A99BE0]/30">
                  {t.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action row */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          data-testid={`team-visited-${lead.id}`}
          onClick={onMarkVisited}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-[11px] font-bold tracking-widest uppercase transition-colors active:scale-95 ${
            visited
              ? 'bg-[#A99BE0] text-white shadow-[0_4px_14px_rgba(169,155,224,0.4)]'
              : 'bg-white border-2 border-[#A99BE0]/40 text-[#7C5DA5]'
          }`}
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          {visited
            ? (lang === 'es' ? 'Visitó stand' : 'Visited')
            : (lang === 'es' ? 'Marcar visita' : 'Mark visit')}
        </button>
        <button
          data-testid={`team-contact-${lead.id}`}
          onClick={onContact}
          className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-[11px] font-bold tracking-widest uppercase text-white transition-transform active:scale-95 ${
            tier === 'hot'
              ? 'bg-[#25D366] shadow-[0_6px_18px_rgba(37,211,102,0.4)] animate-pulse'
              : 'bg-[#25D366]/90'
          }`}
        >
          <MessageCircle className="w-3.5 h-3.5" />
          {lang === 'es' ? 'WhatsApp' : 'WhatsApp'}
        </button>
      </div>
    </article>
  );
};

const TemplatePicker = ({ lead, lang, onClose }) => {
  const tier = (lead.tier || 'cold').toLowerCase();
  const templates = TEMPLATES[tier] || TEMPLATES.cold;
  const first = (lead.name || '').split(/\s+/)[0] || '';
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="team-template-picker"
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 shadow-2xl animate-fade-up"
      >
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#A99BE0]">
            {lang === 'es' ? 'Elige plantilla' : 'Pick template'}
          </p>
          <button onClick={onClose} className="text-xs font-bold tracking-widest uppercase text-[#574A66] hover:text-[#FF2D9C]">
            {lang === 'es' ? 'Cerrar' : 'Close'}
          </button>
        </div>
        <h3 className="font-display text-xl font-black tracking-tighter">
          {lang === 'es' ? `Para ${first}` : `For ${first}`}
          <span className={`ml-2 text-[10px] font-bold tracking-widest uppercase rounded-full px-2 py-0.5 ${tierMeta[tier].pillCls}`}>
            {tierMeta[tier].label}
          </span>
        </h3>
        <div className="mt-4 space-y-3">
          {templates.map((tpl) => {
            const text = tpl[lang === 'en' ? 'en' : 'es'](first);
            return (
              <a
                key={tpl.id}
                data-testid={`template-${tpl.id}`}
                href={buildWaLink(lead.phone, text)}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-2xl border border-[#A99BE0]/20 p-3 hover:border-[#FF2D9C] transition-colors"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-3.5 h-3.5 text-[#FF2D9C]" />
                  <p className="text-[10px] font-bold tracking-widest uppercase text-[#1F1A24]">{tpl.title}</p>
                </div>
                <p className="text-xs text-[#574A66] line-clamp-3">{text}</p>
                <p className="mt-2 text-[10px] font-bold tracking-widest uppercase text-[#25D366] inline-flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" /> {lang === 'es' ? 'Enviar por WhatsApp' : 'Send via WhatsApp'}
                </p>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};
